import { useAuth0, User } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import { Action, Button, FullPageLoader, FullPageStatus } from 'corso-admin-ui';
import { jwtDecode } from 'jwt-decode';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Navigate } from 'react-router-dom';
import { match } from 'ts-pattern';
import { setAccessTokenGetter } from '~/accessToken';
import api from '~/api';
import env from '~/env';
import { useSearchParam } from '~/hooks/useSearchParam';
import { useWidgetAuth, useWidgetRef, WidgetRef } from '~/hooks/widgetHooks';
import { useZendeskParams } from '~/integrations/zendesk/useZendeskParams';
import { ACCESS_CODE_KEY } from '~/types';
import { ErrorResponse } from '~/utils/errorResponse';
import { addBreadcrumb } from '~/utils/observability';
import Auth0ProviderWithNavigate from './Auth0ProviderWithNavigate';
import { CorsoUser, UserProvider } from './UserProvider';

// TODO SPLIT FILE INTO RELEVANT PARTS

type AuthContext = { kind: 'widget' | 'auth0'; onLogout: () => void };

const AuthContext = createContext<null | AuthContext>(null);

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export function useLogout() {
  const context = useAuth();
  return context.onLogout;
}

export function useIsWidgetAuth() {
  const context = useAuth();
  return context.kind === 'widget';
}

function AuthProvider({
  kind,
  user,
  onLogout,
  children,
}: PropsWithChildren<
  AuthContext & {
    user: CorsoUser;
  }
>) {
  const value = useMemo(() => ({ kind, onLogout }), [kind, onLogout]);

  useEffect(() => {
    document.body.dataset.auth = kind;
  }, [kind]);

  // ? potentially children only and AuthContext to absorb the user into context
  return (
    <AuthContext.Provider value={value}>
      <UserProvider user={user}>{children}</UserProvider>
    </AuthContext.Provider>
  );
}

function Auth0IntoAuthProvider({
  auth0UserSub,
  onLogout,
  children,
}: PropsWithChildren<{
  onLogout: () => void;
  auth0UserSub: NonNullable<User['sub']>;
}>) {
  const {
    data: corsoUser,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ['auth0User', auth0UserSub],
    queryFn: () => api.user.get(auth0UserSub),
    retryDelay: 1000,
  });

  if (isPending) return <FullPageLoader />;

  if (isError) {
    console.error('Error Fetching Corso User for Auth0 User', error);
    return (
      <FullPageStatus
        title="Unable to Fetch User"
        details="Please ensure your email exists in Corso, if this issue persists, contact support."
      />
    );
  }

  return (
    <AuthProvider kind="auth0" user={corsoUser} onLogout={onLogout}>
      {children}
    </AuthProvider>
  );
}

function Auth0UserIntoAuthProvider({ children }: PropsWithChildren) {
  const {
    isAuthenticated,
    isLoading,
    error,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    user: auth0User,
  } = useAuth0();

  // registered before any API call so browser requests carry a verifiable Auth0
  // access token; the audience is requested here (not on the provider alone) so
  // it is part of the token grant
  setAccessTokenGetter(() =>
    getAccessTokenSilently({
      authorizationParams: { audience: env.VITE_AUTH0_AUDIENCE },
    }),
  );

  if (isLoading) return <FullPageLoader />; // authenticating with auth0

  if (error) {
    // the Auth0 SDK rejects with this exact message when its token request exceeds the timeout; the session is likely still valid
    if (error.message === "Timeout when executing 'fetch'") {
      addBreadcrumb({
        message: `Handled Auth0 Error: ${error.message}`,
        level: 'warning',
      });
      return (
        <FullPageStatus
          title="Connection Issue"
          details="We couldn't sign you in. Please check your connection and try again."
        >
          <div className="flex flex-col items-center justify-center gap-4">
            <Button
              variant="primary"
              size="lg"
              onClick={() => window.location.reload()}
            >
              Try Again
            </Button>
          </div>
        </FullPageStatus>
      );
    }

    if (
      error.message === 'Invalid state' ||
      error.message === 'Invalid authorization code'
    ) {
      addBreadcrumb({
        message: `Handled Auth0 Error: ${error.message}`,
        level: 'warning',
      });
      // try to login again and recover return to root, invalid state will exist as a `code` query parameter, so all query parameters are omitted
      loginWithRedirect({
        appState: {
          returnTo: `${window.location.pathname}`,
        },
      }).catch((loginWithRedirectError) => {
        console.error(
          new Error('Error with Auth0 Login Redirect', {
            cause: loginWithRedirectError,
          }),
        );
      });
    } else {
      console.error(new Error('Unhandled Auth0 Error', { cause: error }));
    }
    // regardless of error, render an actionable status page
    // ? possibly use PageStatus.Error
    return (
      <FullPageStatus
        title="Session Expired"
        details="Please log in again to continue."
      >
        <div className="flex flex-col items-center justify-center gap-4">
          <Button variant="primary" size="lg" onClick={() => logout()}>
            Continue
          </Button>
        </div>
      </FullPageStatus>
    );
  }

  if (!isAuthenticated || !auth0User) {
    // ? if embedded, use popup login, since auth0 won't allow embedding
    loginWithRedirect({
      appState: {
        returnTo: `${window.location.pathname}${window.location.search}`,
      },
    }).catch((loginWithRedirectError) => {
      console.error(
        new Error('Error with Auth0 Login Redirect', {
          cause: loginWithRedirectError,
        }),
      );
    });
    return <FullPageLoader />;
  }

  if (!auth0User.sub) {
    throw new Error('Not Auth0 Subject');
  }

  return (
    <Auth0IntoAuthProvider auth0UserSub={auth0User.sub} onLogout={logout}>
      {children}
    </Auth0IntoAuthProvider>
  );
}

export function AuthWithAuth0({ children }: PropsWithChildren) {
  return (
    <Auth0ProviderWithNavigate>
      <Auth0UserIntoAuthProvider>{children}</Auth0UserIntoAuthProvider>
    </Auth0ProviderWithNavigate>
  );
}

function UnauthorizedWidget({ email }: { email?: string }) {
  if (email) {
    return (
      <FullPageStatus
        title="Unauthorized"
        details={`Please ensure a user with email "${email}" exists in Corso, if this issue persists, contact support.`}
      />
    );
  }
  return (
    <FullPageStatus
      title="Unauthorized"
      details="Please ensure your email exists in Corso, if this issue persists, contact support."
    />
  );
}

// The Corso session token is a short JWT; re-mint a little before it expires.
function isSessionTokenExpiringSoon(token: string) {
  try {
    const { exp } = jwtDecode(token);
    return exp && exp * 1000 < Date.now() + 1000 * 60;
  } catch {
    return true; // unparseable → force a re-mint
  }
}

// ? maybe double check that the widgetAuth.ref matches widgetRef
export function AuthWithToken({
  widgetRef,
  children,
}: PropsWithChildren<{ widgetRef: WidgetRef }>) {
  const widgetAuth = useWidgetAuth();
  const zendeskParams = useZendeskParams();

  const {
    data: corsoUser,
    isPending,
    error,
    isSuccess,
  } = useQuery({
    enabled: !!widgetAuth,
    queryKey: ['tokenLogin', widgetAuth],
    queryFn: () => {
      if (!widgetAuth) throw new Error('No auth provided for token login');
      // TODO with an error revision, this could throw a CancelledError on 401 to avoid retries
      return api.user.tokenLogin(widgetAuth);
    },
    retryDelay: 1000,
  });

  // widgets have no Auth0 token, so the session token is used and re-minted from widget auth when it nears expiry
  const sessionToken = corsoUser?.sessionToken ?? null;
  const sessionTokenRef = useRef(sessionToken);

  // synchronously register, mitigating/eliminating freshly mounted queries firing tokenless unless actually without a token
  if (sessionToken && widgetAuth) {
    sessionTokenRef.current = sessionToken;
    setAccessTokenGetter(async () => {
      const { current } = sessionTokenRef;
      if (current && !isSessionTokenExpiringSoon(current)) return current;
      const { sessionToken: fresh } = await api.user.tokenLogin(widgetAuth);
      sessionTokenRef.current = fresh;
      return fresh;
    });
  }

  if (!widgetAuth) return <UnauthorizedWidget />;

  if (isPending) return <FullPageLoader />; // TODO use `PageStatus` instead

  if (isSuccess) {
    return (
      <AuthProvider
        kind="widget"
        user={corsoUser.user}
        onLogout={() => {
          // TODO eliminate logout for embedded sessions
          window.location.pathname = '/';
        }}
      >
        {children}
      </AuthProvider>
    );
  }

  if (!(error instanceof ErrorResponse)) {
    return (
      <FullPageStatus
        title="Unable to Authenticate"
        details="An unexpected error occurred. If the issue persists, please contact support."
      />
    );
  }

  return match(widgetRef)
    .with('corso-concierge', () => (
      <UnauthorizedWidget email={widgetAuth.email} />
    ))
    .with('kustomer', () => <UnauthorizedWidget email={widgetAuth.email} />)
    .with('zendesk', () => {
      if (error.response.status === 403) {
        return <UnauthorizedWidget email={widgetAuth.email} />;
      }

      if (!zendeskParams) {
        return <UnauthorizedWidget email={widgetAuth.email} />; // missing data expected to be provided by Zendesk
      }

      return (
        <FullPageStatus
          title="Link for Access"
          details="Thanks for installing! Revisit settings to finish linking your Zendesk workspace to Corso."
        >
          {/* return to standalone app to connect with given params */}
          <Action
            to={{
              pathname: '/',
              search: new URLSearchParams(zendeskParams).toString(),
            }}
            target="_blank"
            variant="primary"
          >
            Connect
          </Action>
        </FullPageStatus>
      );
    })

    .exhaustive();
}

export default function AutoAuthProvider({ children }: PropsWithChildren) {
  const accessCode = useSearchParam(ACCESS_CODE_KEY);

  // memoized so that the auth mechanism is only set/selected once
  const widgetRef = useWidgetRef();

  if (widgetRef) {
    return <AuthWithToken widgetRef={widgetRef}>{children}</AuthWithToken>;
  }

  if (accessCode) {
    return (
      <>
        <FullPageLoader />
        <Navigate to={`/welcome?${ACCESS_CODE_KEY}=${accessCode}`} replace />
      </>
    );
  }
  return <AuthWithAuth0>{children}</AuthWithAuth0>;
}
